import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { Checkbox } from '@hopskipdrive/react-components';
import { analyticsRiderServicesFieldChanged } from '@rideiq/api/services/Analytics';
import { colorTokens, variables } from '@rideiq/shared/components/variables';
import { differenceInYears } from 'date-fns';
import styled from 'styled-components';

import { removeCarSeatFromServices } from '../../../helpers/removeCarSeatFromServices';
import type { RiderFormValues } from '../../../types';

import { SubsectionLabel, SubsectionWrapper } from './styles';

export const SeatOptionsSubsection = () => {
  const { control, getValues, setValue } = useFormContext<RiderFormValues>();
  const { slug } = useParams<{ slug?: string }>();
  const dateOfBirth = useWatch({ control, name: 'dateOfBirth' });

  const age = dateOfBirth ? differenceInYears(Date.now(), new Date(dateOfBirth)) : undefined;
  const frontSeatDisabled = age !== undefined && age <= 11;

  return (
    <SubsectionWrapper data-testid="seat-options-subsection">
      <SubsectionLabel>Seating</SubsectionLabel>
      <CheckboxRow>
        <Controller
          control={control}
          name="frontSeatAuthorized"
          render={({ field: { value, onChange, onBlur } }) => (
            <CheckboxLabel>
              <Checkbox
                checked={!!value}
                onBlur={onBlur}
                disabled={frontSeatDisabled}
                onChange={(e) => {
                  onChange(e.target.checked);
                  if (e.target.checked) {
                    setValue('boosterSeat', false, { shouldDirty: true });
                  }
                  analyticsRiderServicesFieldChanged({
                    field_name: 'frontSeatAuthorized',
                    new_value: e.target.checked,
                    rider_id: slug ?? null,
                  });
                }}
                inputProps={{ 'aria-label': 'Can sit up front' }}
              />
              <span>Can sit up front</span>
            </CheckboxLabel>
          )}
        />
        <Controller
          control={control}
          name="boosterSeat"
          render={({ field: { value, onChange, onBlur } }) => (
            <CheckboxLabel>
              <Checkbox
                checked={!!value}
                onBlur={onBlur}
                onChange={(e) => {
                  onChange(e.target.checked);
                  if (e.target.checked) {
                    setValue('frontSeatAuthorized', false, { shouldDirty: true });
                    removeCarSeatFromServices(getValues, setValue);
                  }
                  analyticsRiderServicesFieldChanged({
                    field_name: 'boosterSeat',
                    new_value: e.target.checked,
                    rider_id: slug ?? null,
                  });
                }}
                inputProps={{ 'aria-label': 'Needs a booster seat' }}
              />
              <span>Needs a booster seat</span>
            </CheckboxLabel>
          )}
        />
      </CheckboxRow>
    </SubsectionWrapper>
  );
};

const mqDesktop = `@media (max-width: ${variables.breakpoints.desktop - 1}px)`;

const CheckboxRow = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  ${mqDesktop} {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
`;

const CheckboxLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  font-size: 13px;
  color: ${colorTokens.text.dark};
`;
